import React, {useState} from "react";
import PropTypes from "prop-types";
import {FormattedMessage} from "react-intl";

import {Alert, Button} from "@mui/material";
import BlockOutlinedIcon from "@mui/icons-material/BlockOutlined";
import LockOpenOutlinedIcon from "@mui/icons-material/LockOpenOutlined";

import BeatLoaderMini from "components/loaders/BeatLoaderMini";

import {formatDateTime} from "views/personalShopper/components/formatters";

import SuspendDialog from "./SuspendDialog";

SuspensionPanel.propTypes = {
    conversation: PropTypes.object.isRequired,
    onSuspend: PropTypes.func.isRequired,
    onReactivate: PropTypes.func.isRequired,
};

/**
 * The access suspended for abusive content (by the assistant or by a member): the assistant
 * stays silent to that number until the team, having checked the words, reactivates it. When
 * not suspended, a discreet way to suspend by hand.
 */
function SuspensionPanel({conversation, onSuspend, onReactivate}) {
    const [busy, setBusy] = useState(false);
    const [error, setError] = useState(null);
    const [dialogOpen, setDialogOpen] = useState(false);

    const run = (action) => {
        setBusy(true);
        setError(null);

        return action()
            .then(() => setDialogOpen(false))
            .catch((requestError) => setError(requestError.message))
            .finally(() => setBusy(false));
    };

    if (!conversation.suspendedAt) {
        return (
            <div style={{display: "flex", justifyContent: "flex-end", marginBottom: "8px"}}>
                <Button size="small" color="error" startIcon={<BlockOutlinedIcon />} disabled={busy} onClick={() => setDialogOpen(true)} sx={{textTransform: "none"}}>
                    <FormattedMessage id="personalShopper.suspension.suspend" />
                </Button>
                <SuspendDialog open={dialogOpen} busy={busy} onConfirm={(reason) => run(() => onSuspend(reason))} onClose={() => setDialogOpen(false)} />
                {error && <Alert severity="error" sx={{marginLeft: "8px"}}>{error}</Alert>}
            </div>
        );
    }

    const by = conversation.suspendedBy === "assistant" ? <FormattedMessage id="personalShopper.suspension.byAssistant" /> : conversation.suspendedBy;

    return (
        <Alert
            severity="error"
            icon={<BlockOutlinedIcon />}
            sx={{marginBottom: "16px", alignItems: "center"}}
            action={
                <Button variant="contained" color="error" startIcon={<LockOpenOutlinedIcon />} disabled={busy} onClick={() => run(onReactivate)} sx={{textTransform: "none", whiteSpace: "nowrap"}}>
                    <FormattedMessage id="personalShopper.suspension.reactivate" />
                    <BeatLoaderMini loading={busy} />
                </Button>
            }
        >
            <div style={{fontWeight: 600}}>
                <FormattedMessage id="personalShopper.suspension.banner" values={{date: formatDateTime(conversation.suspendedAt), by, reason: conversation.suspensionReason || ""}} />
            </div>
            <div style={{fontSize: "13px", marginTop: "4px"}}><FormattedMessage id="personalShopper.suspension.explanation" /></div>
            {error && <div style={{marginTop: "4px"}}>{error}</div>}
        </Alert>
    );
}

export default SuspensionPanel;
